import _ from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { APIResponse, successDummyResponse } from '../../../../lib/commons/api'
import SprintBacklogApi, {
  UpdateBatchSprintBacklogProps,
} from '../../../../lib/functions/sprintBacklog'
import TicketListApi from '../../../../lib/functions/ticketList'
import { WbsItemDeltaInput } from '../../../../lib/functions/wbsItem'
import store from '../../../../store'
import {
  doNotRequireSave,
  requireSave,
} from '../../../../store/requiredSaveData'
import {
  createNewTicketRow,
  createTicketRowByResponse,
  createUpdateTicketDeltaBatchRequest,
  generateUpdateWbsItemDeltaInput,
} from '../dataManager'
import { TicketRow } from '../tickets'
import TicketApi, {
  TicketBasic,
  UpdateTicketDeltaInput,
} from '../../../../lib/functions/ticket'
import { useWbsItemCodeService } from '../../../../services/adaptors/wbsItemCodeServiceAdaptor'

export const useTicketsData = (
  projectUuid: string,
  ticketListUuid: string | undefined
) => {
  const [data, setDataInternal] = useState<TicketRow[]>([])
  const originalData = useRef<TicketRow[]>([])
  const originalDataMapByUuid = useRef<Record<string, TicketRow>>({})
  const [deletedRows, setDeletedRows] = useState<TicketRow[]>([])
  const dispatch = useDispatch()

  const { generateCode } = useWbsItemCodeService()

  // fetch functions ---
  const fetch = useCallback(async (): Promise<TicketRow[]> => {
    if (!ticketListUuid) {
      return []
    }
    const response = await TicketApi.getByTicketListUuid({
      ticketListUuid,
    })
    const { dailyWorkHours, monthlyWorkDays } =
      store.getState().tenant.organization!
    return response?.json?.map(v =>
      createTicketRowByResponse(v, dailyWorkHours, monthlyWorkDays)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketListUuid])

  const refresh = useCallback(async () => {
    const tickets: TicketRow[] = await fetch()
    if (_.isEmpty(tickets) && ticketListUuid) {
      const ticketList: TicketBasic =
        await TicketListApi.getByUuid(ticketListUuid)
      const newRow = createNewTicketRow(ticketList, generateCode)
      newRow && tickets.push(newRow)
    }
    setDataInternal(tickets)
    originalData.current = []
    originalDataMapByUuid.current = {}
    tickets.forEach(ticket => {
      const originalTicket = _.cloneDeep(ticket)
      originalData.current.push(originalTicket)
      originalDataMapByUuid.current[originalTicket.uuid] = originalTicket
    })
    setDeletedRows([])
    store.dispatch(doNotRequireSave())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetch])

  useEffect(() => {
    refresh()
  }, [refresh])

  const refreshSingleRow = useCallback(
    async (wbsItemUuid: string | undefined) => {
      if (!wbsItemUuid) return
      const targetData: TicketRow | undefined = data.find(
        d => d.wbsItem?.uuid === wbsItemUuid
      )
      if (!targetData) return
      const targetUuid = targetData.uuid
      if (!targetUuid) return

      const response = await TicketApi.getDetail({ uuid: targetUuid })
      const { dailyWorkHours, monthlyWorkDays } =
        store.getState().tenant.organization!
      const newRow = createTicketRowByResponse(
        response.json,
        dailyWorkHours,
        monthlyWorkDays
      )
      const newData: TicketRow[] = data.map((row: TicketRow) => {
        if (row.uuid !== newRow.uuid) return row
        return newRow
      })
      const originalDataIndex = originalData.current.findIndex(
        v => v.uuid === newRow.uuid
      )
      if (originalDataIndex >= 0) {
        const newRowCloned = _.cloneDeep(newRow)
        originalData.current[originalDataIndex] = newRowCloned
        originalDataMapByUuid.current[newRow.uuid] = newRowCloned
      } else {
        originalData.current.push(_.cloneDeep(newRow))
        originalDataMapByUuid.current[newRow.uuid] = _.cloneDeep(newRow)
      }
      setDataInternal(newData)
    },
    [data]
  )

  // save functions ---
  const saveSprintBacklog = useCallback(
    async (
      changed: TicketRow[],
      wbsItemLockVersionMap: { [wbsItemUuid: string]: number }
    ): Promise<APIResponse> => {
      if (_.isEmpty(changed)) return successDummyResponse
      const sprintBacklogRequest: UpdateBatchSprintBacklogProps = {
        sprintBacklogItems: [],
        sprintPlans: {
          added: [],
          deleted: [],
        },
      }
      changed.forEach(row => {
        const wbsItem = row.wbsItem!
        const sprint = wbsItem.sprint
        const originalSprint =
          originalDataMapByUuid.current[row.uuid]?.wbsItem?.sprint
        if (!wbsItem?.uuid) return
        if (
          !!sprint &&
          (row.added ||
            (row.edited &&
              (!originalSprint || originalSprint.uuid !== sprint.uuid)))
        ) {
          sprintBacklogRequest.sprintPlans.added.push({
            taskUuid: wbsItem.uuid,
            taskLockVersion:
              wbsItemLockVersionMap[wbsItem.uuid] ?? wbsItem.lockVersion!,
            sprintUuid: sprint.uuid,
          })
        } else if (row.edited && !sprint && !!originalSprint) {
          sprintBacklogRequest.sprintPlans.deleted.push({
            taskUuid: wbsItem.uuid,
            taskLockVersion:
              wbsItemLockVersionMap[wbsItem.uuid] ?? wbsItem.lockVersion!,
            sprintUuid: originalSprint.uuid,
          })
        }
      })
      if (
        _.isEmpty(sprintBacklogRequest.sprintPlans.added) &&
        _.isEmpty(sprintBacklogRequest.sprintPlans.deleted)
      ) {
        return successDummyResponse
      }
      return SprintBacklogApi.updateBatchSprintBacklog(sprintBacklogRequest)
    },
    []
  )

  const updateTickets = useCallback(
    async (rows: TicketRow[], deleteTargetRows: TicketRow[]) => {
      const request = createUpdateTicketDeltaBatchRequest(
        projectUuid,
        rows,
        originalData.current,
        data,
        deleteTargetRows
      )

      const filteredRequest = {
        ...request,
        edited: request.edited.filter(
          (item): item is UpdateTicketDeltaInput => item !== undefined
        ),
      }

      const response = await TicketApi.updateBatchDelta(filteredRequest)

      // Update sprint backlogs
      const wbsItemLockVersionMap = Object.fromEntries(
        [...response.json?.added, ...response.json?.edited].map(v => [
          v.wbsItemUuid,
          v.wbsItemLockVersion,
        ])
      )
      const sprintResponse = await saveSprintBacklog(
        data.filter(row => row.added || row.edited),
        wbsItemLockVersionMap
      )
      return [response, sprintResponse]
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectUuid, data, saveSprintBacklog]
  )

  const save = useCallback(async (): Promise<APIResponse[]> => {
    return updateTickets(
      data.filter(v => v.added || v.edited),
      deletedRows
    )
  }, [data, deletedRows, updateTickets])

  const generateWbsItemDeltaRequest = useCallback(
    (row: TicketRow): WbsItemDeltaInput | undefined => {
      const original = originalDataMapByUuid.current[row.uuid]
      if (row?.added || !row?.wbsItem || !original?.wbsItem) return undefined
      return generateUpdateWbsItemDeltaInput(original.wbsItem, row.wbsItem)
    },
    []
  )

  const saveSingleRow = useCallback(
    async (uuid: string) => {
      await updateTickets(
        data.filter(v => v.uuid === uuid && (v.added || v.edited)),
        []
      )
    },
    [data, updateTickets]
  )

  // update data functions ---
  const setData = useCallback(
    (data: TicketRow[]) => {
      setDataInternal(data)
      dispatch(requireSave())
    },
    [dispatch]
  )

  const clearData = useCallback(() => {
    setDataInternal([])
  }, [])

  const deleteRows = useCallback(
    (rows: TicketRow[]) => {
      setDeletedRows([...deletedRows, ...rows.filter(v => !v.added)])
    },
    [deletedRows]
  )

  const currentData = useRef<TicketRow[]>()
  useEffect(() => {
    currentData.current = data
  }, [data])
  const hasDataTicketTypeIsChanged = useCallback((): boolean => {
    if (!currentData.current) return false
    return currentData.current.some(current => {
      const original = originalDataMapByUuid.current[current.uuid]
      return (
        original &&
        !current.added &&
        current.edited &&
        current.ticketList?.ticketType !== original.ticketList?.ticketType
      )
    })
  }, [])

  return {
    data,
    setData,
    clearData,
    refresh,
    refreshSingleRow,
    save,
    saveSingleRow,
    generateWbsItemDeltaRequest,
    deleteRows,
    hasDataTicketTypeIsChanged,
  }
}
